import React from 'react';

import { currentWinner, isDrawn, type SetValue, type Winner } from './scoring-match-draft';
import { matchPoints } from './scoring-match-points';
import { shortenSideName } from './scoring-side-name';
import {
  deltaTone,
  formatDelta,
  type MatchItem,
  type MatchRating,
  type ScoringSystem,
} from './scoring-types';

// Delta z księgi idzie na ekran tylko, gdy szkic pokrywa się z zapisanym wynikiem —
// szkic innego wyniku nad zapisanym meczem pożyczyłby cudzą deltę. Remis nie ma
// swoich widełek, więc bez wpisu w księdze nie pokazuje nic.
function appliedDelta(
  match: MatchItem,
  rating: MatchRating,
  side: 1 | 2,
  winner: Winner,
  drawn: boolean,
): number | null {
  if (rating.appliedDelta === null) {
    return null;
  }

  const draftMatchesSaved = winner === null ? drawn && isDrawn(match) : currentWinner(match) === winner;

  if (!draftMatchesSaved) {
    return null;
  }

  return side === 1 ? rating.appliedDelta.side1 : rating.appliedDelta.side2;
}

// Widełki mówią o ZWYKŁEJ wygranej sprzed meczu; na better point rozegrany mecz
// waży dominację, więc zapisany wynik musi pokazać to, co księga naprawdę naliczyła.
// Walkower nie trafia do księgi, więc nie ma czym straszyć ani kusić.
function sideDelta(
  match: MatchItem,
  side: 1 | 2,
  winner: Winner,
  drawn: boolean,
  walkover: boolean,
): number | null {
  const rating = match.rating;

  if (!rating || walkover) {
    return null;
  }

  const applied = appliedDelta(match, rating, side, winner, drawn);

  if (applied !== null || winner === null) {
    return applied;
  }

  const view = side === 1 ? rating.side1 : rating.side2;

  return (winner === side ? view.deltaIfWin : view.deltaIfLose) ?? null;
}

// Remis wyróżnia obie strony — po samym braku pogrubienia nie dałoby się go
// odróżnić od meczu, którego nikt jeszcze nie rozegrał.
function sideModifier(side: 1 | 2, winner: Winner, drawn: boolean): string {
  if (drawn) {
    return ' scoring-side--drawn';
  }

  if (winner === side) {
    return ' scoring-side--won';
  }

  return '';
}

interface RatingCellProps {
  match: MatchItem;
  side: 1 | 2;
  delta: number | null;
}

const RatingCell: React.FC<RatingCellProps> = ({ match, side, delta }) => {
  const rating = side === 1 ? match.rating?.side1 : match.rating?.side2;

  if (!rating) {
    return null;
  }

  return (
    <span
      className="scoring-delta scoring-delta--rating"
      title="Ranking przed tą kolejką i zmiana po tym meczu"
      data-testid={`match-${match.id}-rating${side}`}
    >
      {rating.rating}
      {delta !== null && (
        <span className={deltaTone(delta)} data-testid={`match-${match.id}-delta${side}`}>
          {formatDelta(delta)}
        </span>
      )}
    </span>
  );
};

interface SideRowProps {
  match: MatchItem;
  side: 1 | 2;
  sets: SetValue[];
  winner: Winner;
  points: number | null;
  showRating: boolean;
  showSets: boolean;
  compact: boolean;
  drawn: boolean;
  numbered: boolean;
}

const SideRow: React.FC<SideRowProps> = ({
  match,
  side,
  sets,
  winner,
  points,
  showRating,
  showSets,
  compact,
  drawn,
  numbered,
}) => {
  const scored = sets.filter((set) => set.p1 !== '' && set.p2 !== '');
  const walkover = scored.length === 0 && winner !== null;
  const delta = sideDelta(match, side, winner, drawn, walkover);
  const name = side === 1 ? match.player1Name : match.player2Name;

  return (
    <div
      className={`scoring-side${sideModifier(side, winner, drawn)}${numbered ? ` scoring-side--tinted-${side}` : ''}`}
    >
      {numbered && (
        <span
          className={`scoring-side__number scoring-side__number--${side}`}
          data-testid={`match-${match.id}-side${side}-number`}
        >
          {side}
        </span>
      )}
      <span className="scoring-side__name" data-testid={`match-${match.id}-p${side}`}>
        {compact ? shortenSideName(name) : name}
      </span>
      {showSets && (
        <span className="scoring-side__sets">
          {scored.length === 0
            ? (walkover ? 'wo' : '—')
            : scored.map((set, index) => (
                <span key={index} className="scoring-side__set">
                  {side === 1 ? set.p1 : set.p2}
                </span>
              ))}
        </span>
      )}
      {points !== null && (
        <span
          className="scoring-side__points"
          title="Punkty klasyfikacyjne za sety"
          data-testid={`match-${match.id}-points${side}`}
        >
          {points}
          <span className="scoring-side__points-unit">pkt</span>
        </span>
      )}
      {showRating && <RatingCell match={match} side={side} delta={delta} />}
    </div>
  );
};

interface Props {
  match: MatchItem;
  sets: SetValue[];
  winner: Winner;
  scoring?: ScoringSystem;
  showRating?: boolean;
  compact?: boolean;
  drawn?: boolean;
  numbered?: boolean;
}

export const ScoringMatchSides: React.FC<Props> = ({
  match,
  sets,
  winner,
  scoring,
  showRating = true,
  compact = false,
  drawn = false,
  numbered = false,
}) => {
  const points = matchPoints(scoring, sets);
  // Przy punktacji za sety wynik niesie kolumna "pkt", więc na telefonie sety
  // są powtórzeniem. Bez niej sety są jedynym śladem tego, jak mecz się skończył.
  const showSets = !compact || scoring?.perSet !== true;

  return (
    <div className="scoring-match__sides">
      <SideRow
        match={match}
        side={1}
        sets={sets}
        winner={winner}
        points={points?.side1 ?? null}
        showRating={showRating}
        showSets={showSets}
        compact={compact}
        drawn={drawn}
        numbered={numbered}
      />
      <SideRow
        match={match}
        side={2}
        sets={sets}
        winner={winner}
        points={points?.side2 ?? null}
        showRating={showRating}
        showSets={showSets}
        compact={compact}
        drawn={drawn}
        numbered={numbered}
      />
    </div>
  );
};

export default ScoringMatchSides;
